import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  Trophy,
  Calendar,
  Activity,
  Plus,
  AlertTriangle,
  CheckCircle2,
  Minus,
  Play,
  Trash2,
  Pencil,
  FileUp,
  Info,
  Shield,
  Users,
  ClipboardList
} from 'lucide-react';
import { collection, onSnapshot, query, where, addDoc, doc, setDoc, deleteDoc, deleteField } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import { PITCH_COUNT_RULESETS, getPitcherEligibility, buildCustomTournamentRules } from '../lib/pitch-count-rules';
import { getTournamentCapacity } from '../lib/pitching-intel';
import {
  checkBenchPlan,
  checkRoundRobinPitching,
  getBenchTarget,
  getBattingSlots,
  type BenchSketch,
  type RoundRobinPlan,
} from '../lib/tournament-bench-plan';

interface TournamentViewProps {
  tournament: any;
  team: any;
  teamId: string;
  players: any[];
  onClose: () => void;
  onOpenGame: (game: any) => void;
  onEdit: () => void;
  onImportStats?: (game: any) => void;
  onEnterResults?: (game: any) => void;
  onPlanGame?: (game: any) => void;
  allGames?: any[];
}

export const TournamentView = ({ tournament, team, teamId, players, onClose, onOpenGame, onEdit, onImportStats, onEnterResults, onPlanGame, allGames = [] }: TournamentViewProps) => {
  const [games, setGames] = useState<any[]>([]);
  const [activeSection, setActiveSection] = useState<'games' | 'pitching' | 'roundrobin'>('games');
  // Coach's working round-robin plan (per game: starting pitcher, relief, bench).
  // Persisted on the tournament doc as `roundRobinPlan`; committed to game plans
  // on Apply. Migrates the older `benchPlan` shape (startingBenchIds only).
  const [roundRobinPlan, setRoundRobinPlan] = useState<RoundRobinPlan>(() => {
    if (tournament.roundRobinPlan) return tournament.roundRobinPlan;
    if (tournament.benchPlan) {
      const migrated: RoundRobinPlan = {};
      Object.entries(tournament.benchPlan).forEach(([gid, v]: [string, any]) => {
        migrated[gid] = { startingBenchIds: v?.startingBenchIds || [] };
      });
      return migrated;
    }
    return {};
  });
  const [benchApplyState, setBenchApplyState] = useState<'idle' | 'saving' | 'done'>('idle');
  const [isAddingGame, setIsAddingGame] = useState<{ phase: 'pool' | 'bracket', slot?: number } | null>(null);
  const [editingGameId, setEditingGameId] = useState<string | null>(null);
  const [deleteStep, setDeleteStep] = useState(0); // 0: none, 1: first confirm, 2: second confirm
  const [enforceGlobalRest, setEnforceGlobalRest] = useState(tournament.enforceGlobalRest ?? false);

  useEffect(() => {
    const q = query(
      collection(db, `teams/${teamId}/games`),
      where('tournamentId', '==', tournament.id)
    );
    return onSnapshot(q, (snapshot) => {
      setGames(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [teamId, tournament.id]);

  const poolGames = games.filter(g => g.tournamentPhase === 'pool').sort((a, b) => (a.tournamentGameNumber || 0) - (b.tournamentGameNumber || 0));
  const bracketGames = games.filter(g => g.tournamentPhase === 'bracket').sort((a, b) => (a.tournamentGameNumber || 0) - (b.tournamentGameNumber || 0));

  const poolSlots = tournament.format === '2_pool_bracket' ? 2 : tournament.format === '3_pool_bracket' ? 3 : 0;

  const handleAddGame = async (phase: 'pool' | 'bracket', slot?: number) => {
    const gameNumber = slot || (games.length + 1);
    const newGame = {
      opponent: 'TBD',
      date: new Date().toISOString(),
      location: '',
      status: 'scheduled',
      tournamentId: tournament.id,
      tournamentPhase: phase,
      tournamentGameNumber: gameNumber,
      createdAt: new Date().toISOString()
    };

    try {
      const docRef = await addDoc(collection(db, `teams/${teamId}/games`), newGame);
      setIsAddingGame(null);
      setEditingGameId(docRef.id);
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, `teams/${teamId}/games`);
    }
  };

  const updateGame = async (gameId: string, data: any) => {
    try {
      await setDoc(doc(db, `teams/${teamId}/games`, gameId), data, { merge: true });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `teams/${teamId}/games/${gameId}`);
    }
  };

  // --- Round Robin Plan Logic ---
  const persistRoundRobinPlan = async (next: RoundRobinPlan) => {
    // Firestore rejects undefined (e.g. un-picking a starter). Turn those into
    // deleteField() so the cleared value is actually removed under merge.
    const payload: Record<string, any> = {};
    for (const [gameId, plan] of Object.entries(next)) {
      payload[gameId] = Object.fromEntries(
        Object.entries(plan || {}).map(([k, v]) => [k, v === undefined ? deleteField() : v])
      );
    }
    try {
      await setDoc(
        doc(db, `teams/${teamId}/tournaments`, tournament.id),
        { roundRobinPlan: payload },
        { merge: true }
      );
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `teams/${teamId}/tournaments/${tournament.id}`);
    }
  };

  // Merge a partial update into one game's plan and persist.
  const updateGamePlan = (gameId: string, patch: Partial<RoundRobinPlan[string]>) => {
    setRoundRobinPlan(prev => {
      const next = { ...prev, [gameId]: { ...prev[gameId], ...patch } };
      persistRoundRobinPlan(next);
      return next;
    });
  };

  const setStartingPitcher = (gameId: string, playerId: string) => {
    const current = roundRobinPlan[gameId]?.startingPitcherId;
    updateGamePlan(gameId, { startingPitcherId: current === playerId ? undefined : playerId });
  };

  const toggleRelief = (gameId: string, playerId: string) => {
    const current = roundRobinPlan[gameId]?.reliefIds || [];
    const next = current.includes(playerId)
      ? current.filter(id => id !== playerId)
      : [...current, playerId];
    updateGamePlan(gameId, { reliefIds: next });
  };

  const toggleBenchRelief = (gameId: string, playerId: string) => {
    const current = roundRobinPlan[gameId]?.benchReliefIds || [];
    const next = current.includes(playerId)
      ? current.filter(id => id !== playerId)
      : [...current, playerId];
    updateGamePlan(gameId, { benchReliefIds: next });
  };

  const toggleBench = (gameId: string, playerId: string) => {
    const current = roundRobinPlan[gameId]?.startingBenchIds || [];
    const next = current.includes(playerId)
      ? current.filter(id => id !== playerId)
      : [...current, playerId];
    updateGamePlan(gameId, { startingBenchIds: next });
  };

  // Apply the round-robin plan to each game's plan so it flows into the single-game
  // planner. Writes startingPitcherId + startingBenchIds (bench picks UNION any
  // relievers marked bench-to-start). These benched kids are active players who
  // start on the BENCH (B), not absent — we never touch absentPlayerIds here.
  const applyRoundRobinToGames = async (tournamentGames: any[]) => {
    setBenchApplyState('saving');
    try {
      await Promise.all(
        tournamentGames.map(g => {
          const gp = roundRobinPlan[g.id] || {};
          const startingBenchIds = Array.from(
            new Set([...(gp.startingBenchIds || []), ...(gp.benchReliefIds || [])])
          );
          const planPatch: any = { startingBenchIds };
          if (gp.startingPitcherId) planPatch.startingPitcherId = gp.startingPitcherId;
          return setDoc(
            doc(db, `teams/${teamId}/games`, g.id),
            { plan: planPatch },
            { merge: true }
          );
        })
      );
      setBenchApplyState('done');
      setTimeout(() => setBenchApplyState('idle'), 2500);
    } catch (error) {
      setBenchApplyState('idle');
      handleFirestoreError(error, OperationType.UPDATE, `teams/${teamId}/games`);
    }
  };

  // --- Pitching Dashboard Logic ---
  const getTournamentPitchingStats = () => {
    const stats: Record<string, { total: number; daily: Record<string, number>; gameBreakdown: any[] }> = {};
    games.forEach(game => {
      if (!game.pitchCounts) return;
      const dateKey = format(new Date(game.date), 'yyyy-MM-dd');
      Object.entries(game.pitchCounts).forEach(([playerId, count]) => {
        if (!stats[playerId]) stats[playerId] = { total: 0, daily: {}, gameBreakdown: [] };
        stats[playerId].total += count as number;
        stats[playerId].daily[dateKey] = (stats[playerId].daily[dateKey] || 0) + (count as number);
        stats[playerId].gameBreakdown.push({ gameId: game.id, opponent: game.opponent, count, phase: game.tournamentPhase });
      });
    });
    return stats;
  };

  const pitchingStats = getTournamentPitchingStats();

  // Season S% and P/BF — weighted across all season games, not just this tournament
  const seasonPitchingStats = useMemo(() => {
    const normalizeRate = (val: any): number | null => {
      if (val == null || val === '') return null;
      const n = parseFloat(val);
      if (isNaN(n)) return null;
      return n > 1 ? n / 100 : n;
    };
    const acc: Record<string, { sumSPct: number; totalPitches: number; sumPBF: number; totalBF: number }> = {};
    allGames.forEach(game => {
      if (!game.stats) return;
      Object.entries(game.stats).forEach(([pid, s]: [string, any]) => {
        if (!s || (!(s.ip > 0) && !(s.pitches > 0))) return;
        if (!acc[pid]) acc[pid] = { sumSPct: 0, totalPitches: 0, sumPBF: 0, totalBF: 0 };
        const pitches = s.pitches || 0;
        const bf = parseInt(s.bf) || Math.round((s.ip || 0) * 4) || 1;
        const sPct = normalizeRate(s.sPct);
        const p_bf = s.p_bf != null ? parseFloat(s.p_bf) : null;
        if (sPct != null) { acc[pid].sumSPct += sPct * pitches; acc[pid].totalPitches += pitches; }
        if (p_bf != null) { acc[pid].sumPBF += p_bf * bf; acc[pid].totalBF += bf; }
      });
    });
    const result: Record<string, { sPct: number | null; p_bf: number | null }> = {};
    Object.keys(acc).forEach(pid => {
      const a = acc[pid];
      result[pid] = {
        sPct: a.totalPitches > 0 ? a.sumSPct / a.totalPitches : null,
        p_bf: a.totalBF > 0 ? a.sumPBF / a.totalBF : null,
      };
    });
    return result;
  }, [allGames]);

  const handleDeleteTournament = async () => {
    try {
      // Delete all games associated with the tournament
      const deletePromises = games.map(game => deleteDoc(doc(db, `teams/${teamId}/games`, game.id)));
      await Promise.all(deletePromises);
      
      // Delete the tournament itself
      await deleteDoc(doc(db, `teams/${teamId}/tournaments`, tournament.id));
      onClose();
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, `teams/${teamId}/tournaments/${tournament.id}`);
    }
  };

  const now = new Date();
  const effectiveStatus = now > new Date(tournament.endDate) ? 'completed'
    : now >= new Date(tournament.startDate) ? 'active'
    : 'upcoming';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-surface flex flex-col"
    >
      <header className="safe-top p-4 sm:p-8 border-b border-hairline bg-surface backdrop-blur-xl sticky top-0 z-20 flex flex-col space-y-6">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-5">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gold rounded-[1.5rem] flex items-center justify-center shadow-[0_0_40px_rgba(245,158,11,0.2)] group">
              <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-surface group-hover:scale-110 transition-transform" />
            </div>
            <div>
              <h2 className="text-xl sm:text-3xl font-black italic uppercase text-ink tracking-tighter leading-none mb-1">{tournament.name}</h2>
              <div className="flex flex-col space-y-2">
                <div className={`flex items-center space-x-1.5 px-2 py-0.5 rounded border w-fit ${
                  effectiveStatus === 'completed' ? 'bg-hairline border-ink-mute' :
                  effectiveStatus === 'active' ? 'bg-canvas border-hairline' :
                  'bg-gold-soft border-hairline'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    effectiveStatus === 'completed' ? 'bg-ink-2' :
                    effectiveStatus === 'active' ? 'bg-ink animate-pulse' :
                    'bg-gold animate-pulse'
                  }`} />
                  <span className={`text-[9px] font-black uppercase tracking-widest ${
                    effectiveStatus === 'completed' ? 'text-ink-2' :
                    effectiveStatus === 'active' ? 'text-accent-ink' :
                    'text-gold-ink'
                  }`}>{effectiveStatus}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="flex flex-col items-center bg-raised border border-ink-mute rounded-xl px-3 py-1.5 leading-none">
                    <span className="text-[8px] font-semibold uppercase tracking-widest text-ink-2">{format(new Date(tournament.startDate), 'MMM')}</span>
                    <span className="text-xl font-black text-ink tabular-nums">{format(new Date(tournament.startDate), 'd')}</span>
                  </div>
                  <span className="text-ink-2 font-black text-sm">—</span>
                  <div className="flex flex-col items-center bg-raised border border-ink-mute rounded-xl px-3 py-1.5 leading-none">
                    <span className="text-[8px] font-semibold uppercase tracking-widest text-ink-2">{format(new Date(tournament.endDate), 'MMM')}</span>
                    <span className="text-xl font-black text-ink tabular-nums">{format(new Date(tournament.endDate), 'd')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-3">
            <button
              onClick={onEdit}
              className="p-3 bg-canvas rounded-2xl text-ink-2 hover:text-ink flex items-center space-x-2 px-4 border border-hairline hover:border-ink-mute transition-all shadow-lg"
              title="Edit Tournament"
            >
              <Pencil className="w-4 h-4" />
              <span className="text-[10px] font-semibold uppercase tracking-widest hidden sm:inline">Edit</span>
            </button>
            <button 
              onClick={() => setDeleteStep(1)}
              className="p-3 bg-canvas rounded-2xl text-ink-2 hover:text-danger transition-colors border border-hairline hover:border-hairline shadow-lg"
              title="Delete Tournament"
            >
              <Trash2 className="w-5 h-5" />
            </button>
            <button onClick={onClose} className="p-3 bg-canvas rounded-2xl text-ink-2 hover:text-ink border border-hairline hover:border-ink-mute transition-all shadow-lg">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {deleteStep > 0 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-surface/90 backdrop-blur-sm flex items-center justify-center p-6"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="max-w-sm w-full bg-canvas border border-hairline rounded-[2.5rem] p-8 space-y-6 text-center shadow-2xl"
            >
              <div className="w-20 h-20 bg-[#FBF0F0] rounded-3xl flex items-center justify-center mx-auto">
                <AlertTriangle className="w-10 h-10 text-danger" />
              </div>
              
              <div className="space-y-2">
                <h3 className="text-2xl font-black italic uppercase tracking-tight">
                  {deleteStep === 1 ? 'Delete Tournament?' : 'Are you SURE?'}
                </h3>
                <p className="text-ink-2 text-sm font-medium leading-relaxed">
                  {deleteStep === 1 
                    ? "This will permanently delete the tournament and all associated games. This action cannot be undone."
                    : "Final warning: All pitch counts, stats, and game data for this tournament will be lost forever."}
                </p>
              </div>

              <div className="space-y-3">
                <button 
                  onClick={() => deleteStep === 1 ? setDeleteStep(2) : handleDeleteTournament()}
                  className="w-full py-4 bg-red-500 text-surface font-black rounded-2xl uppercase italic shadow-[0_0_30px_rgba(239,68,68,0.2)]"
                >
                  {deleteStep === 1 ? 'Yes, Delete Everything' : 'I am Sure, Delete Now'}
                </button>
                <button 
                  onClick={() => setDeleteStep(0)}
                  className="w-full py-4 bg-raised text-ink-2 font-black rounded-2xl uppercase italic"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <nav className="flex items-center space-x-2 p-4 bg-surface border-b border-hairline">
        {[
          { id: 'games', icon: Calendar, label: 'Games' },
          { id: 'roundrobin', icon: Users, label: 'Round Robin' },
          { id: 'pitching', icon: Activity, label: 'Pitching' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id as any)}
            className={cn(
              "flex-1 py-4 rounded-2xl flex items-center justify-center space-x-3 text-[10px] font-black uppercase tracking-[0.2em] transition-all relative overflow-hidden group",
              activeSection === tab.id 
                ? "bg-gold text-surface shadow-xl shadow-amber-500/20" 
                : "text-ink-2 hover:bg-canvas hover:text-ink-2"
            )}
          >
            <tab.icon className={cn("w-4 h-4 transition-transform group-hover:scale-110", activeSection === tab.id ? "fill-current/20" : "")} />
            <span>{tab.label}</span>
            {activeSection === tab.id && (
              <motion.div 
                layoutId="activeTab"
                className="absolute inset-0 bg-ink/10 pointer-events-none"
              />
            )}
          </button>
        ))}
      </nav>

      <main className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl mx-auto space-y-8">
          {activeSection === 'games' && (
            <div className="space-y-8">
              {/* Pool Play Section — hidden for single elimination */}
              {tournament.format !== 'single_elimination' && <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-semibold text-blue-500 uppercase tracking-widest flex items-center space-x-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                    <span>Pool Play</span>
                  </h3>
                </div>
                <div className="grid gap-3">
                  {Array.from({ length: poolSlots }).map((_, i) => {
                    const gameNumber = i + 1;
                    const game = poolGames.find(g => g.tournamentGameNumber === gameNumber);
                    
                    if (!game) {
                      return (
                        <button 
                          key={`empty-pool-${i}`}
                          onClick={() => handleAddGame('pool', gameNumber)}
                          className="w-full p-6 bg-canvas border-2 border-dashed border-hairline rounded-3xl flex flex-col items-center justify-center space-y-2 hover:border-ink-mute transition-colors group"
                        >
                          <Plus className="w-6 h-6 text-ink-2 group-hover:text-ink-2" />
                          <span className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Add Pool Game {gameNumber}</span>
                        </button>
                      );
                    }

                    return (
                      <TournamentGameCard 
                        key={`pool-${game.id}-${i}`} 
                        game={game} 
                        isEditing={editingGameId === game.id}
                        onEdit={() => setEditingGameId(game.id)}
                        onCancel={() => setEditingGameId(null)}
                        onOpen={() => onOpenGame(game)}
                        onUpdate={(data) => {
                          updateGame(game.id, data);
                          setEditingGameId(null);
                        }}
                        onImportStats={onImportStats}
                        onEnterResults={onEnterResults}
                        onPlan={onPlanGame ? () => onPlanGame(game) : undefined}
                        onAddAnother={gameNumber < poolSlots ? () => {
                          // The actual logic should be: find/create next
                          const nextSlot = gameNumber + 1;
                          const nextGame = poolGames.find(g => g.tournamentGameNumber === nextSlot);
                          if (nextGame) {
                            setEditingGameId(nextGame.id);
                          } else {
                            handleAddGame('pool', nextSlot);
                          }
                        } : undefined}
                      />
                    );
                  })}
                </div>
              </div>}

              {/* Bracket Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-semibold text-danger uppercase tracking-widest flex items-center space-x-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full" />
                    <span>Bracket</span>
                  </h3>
                  <button 
                    onClick={() => handleAddGame('bracket')}
                    className="flex items-center space-x-2 px-3 py-1.5 bg-canvas border border-hairline rounded-lg text-[10px] font-black uppercase text-ink-2 hover:text-ink transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                    <span>Add Game</span>
                  </button>
                </div>
                <div className="grid gap-3">
                  {bracketGames.map((game, i) => (
                    <TournamentGameCard 
                      key={`bracket-${game.id}-${i}`} 
                      game={game} 
                      isEditing={editingGameId === game.id}
                      onEdit={() => setEditingGameId(game.id)}
                      onCancel={() => setEditingGameId(null)}
                      onOpen={() => onOpenGame(game)}
                      onUpdate={(data) => {
                        updateGame(game.id, data);
                        setEditingGameId(null);
                      }}
                      onImportStats={onImportStats}
                      onEnterResults={onEnterResults}
                      onPlan={onPlanGame ? () => onPlanGame(game) : undefined}
                    />
                  ))}
                  {bracketGames.length === 0 && (
                    <div className="p-8 text-center bg-canvas/10 rounded-3xl border border-dashed border-hairline">
                      <p className="text-ink-2 text-[10px] font-semibold uppercase tracking-widest italic">Advance from pool play to start the bracket</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'roundrobin' && (() => {
            const tournamentGames = [...poolGames, ...bracketGames];
            const benchTarget = getBenchTarget(players.length, team.settings);
            const battingSlots = getBattingSlots(team.settings);
            const absentByGame: Record<string, string[]> = {};
            tournamentGames.forEach(g => {
              absentByGame[g.id] = g.plan?.absentPlayerIds || [];
            });
            // Bench checker reads the round-robin plan's startingBenchIds.
            const benchSketch: BenchSketch = {};
            tournamentGames.forEach(g => {
              benchSketch[g.id] = { startingBenchIds: roundRobinPlan[g.id]?.startingBenchIds || [] };
            });
            const benchResult = checkBenchPlan(players, tournamentGames, team.settings, benchSketch, {
              tournament, team, absentByGame,
            });
            const benchByGame = new Map(benchResult.perGame.map(r => [r.gameId, r]));
            const pitchResult = checkRoundRobinPitching(players, tournamentGames, roundRobinPlan, { tournament, team });
            const sortedRoster = [...players].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            // Arms = anyone with a P rating or any pitching attribute.
            const arms = sortedRoster.filter(p =>
              (p.positions?.P ?? p.ratings?.P ?? 0) > 0 ||
              ((p.attributes?.pitching?.velocity || 0) + (p.attributes?.pitching?.control || 0)) > 0
            );

            if (tournamentGames.length === 0) {
              return (
                <div className="p-8 text-center bg-canvas rounded-3xl border border-dashed border-hairline">
                  <Users className="w-6 h-6 text-ink-2 mx-auto mb-3" />
                  <p className="text-ink-2 text-[11px] font-semibold uppercase tracking-widest">Add games first</p>
                  <p className="text-ink-2 text-[10px] font-bold mt-2 max-w-xs mx-auto leading-relaxed">
                    Set up your tournament games, then plan your starters, relief, and bench for the weekend.
                  </p>
                </div>
              );
            }

            const statusDot = (status?: string) =>
              status === 'resting' ? 'bg-danger' : status === 'capped' ? 'bg-ink-2' : 'bg-accent-ink';

            return (
              <div className="space-y-6">
                {/* Intro */}
                <div className="bg-canvas rounded-2xl border border-hairline p-5">
                  <p className="text-xs font-black text-ink uppercase tracking-tight">Manage the Round Robin</p>
                  <p className="text-[10px] font-bold text-ink-2 mt-2 leading-relaxed">
                    Set your starting pitcher and relief plan for each game now, so Saturday is just execution. We check arm rest and the tournament pitch cap across all your games.
                  </p>
                </div>

                {/* Per-game cards */}
                <div className="space-y-4">
                  {tournamentGames.map(game => {
                    const gp = roundRobinPlan[game.id] || {};
                    const pitchWarnings = pitchResult.warningsByGame[game.id] || [];
                    const avail = pitchResult.availabilityByGame[game.id] || {};
                    const benchInfo = benchByGame.get(game.id);
                    const benchedIds = gp.startingBenchIds || [];
                    const gameBenchTarget = benchInfo?.benchTarget ?? benchTarget;
                    const reliefIds = gp.reliefIds || [];
                    const benchReliefIds = gp.benchReliefIds || [];

                    return (
                      <div key={`rr-${game.id}`} className="bg-surface rounded-3xl border border-hairline overflow-hidden shadow-sm">
                        <div className="px-5 py-4 border-b border-raised bg-canvas">
                          <p className="text-[11px] font-black text-ink uppercase tracking-tight">
                            {game.tournamentPhase === 'bracket' ? 'Bracket' : 'Pool'} · vs {game.opponent || 'TBD'}
                          </p>
                          <p className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest mt-1">
                            {gp.startingPitcherId ? `SP: ${players.find(p => p.id === gp.startingPitcherId)?.name || '—'}` : 'No starter set'}
                          </p>
                        </div>

                        {/* Pitching warnings */}
                        {pitchWarnings.length > 0 && (
                          <div className="px-5 pt-4 space-y-2">
                            {pitchWarnings.map((w, i) => (
                              <div key={i} className={cn(
                                "rounded-xl p-3 flex items-start space-x-2 border",
                                w.severity === 'red' ? "bg-[#FBF0F0] border-[#F0D0D0]" : "bg-[#FFF8E1] border-hairline"
                              )}>
                                <AlertTriangle className={cn("w-3.5 h-3.5 shrink-0 mt-0.5", w.severity === 'red' ? "text-danger" : "text-gold-ink")} />
                                <p className={cn("text-[10px] font-bold leading-relaxed", w.severity === 'red' ? "text-danger" : "text-[#7A6320]")}>{w.message}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Starting Pitcher */}
                        <div className="p-4 border-b border-raised">
                          <p className="text-[10px] font-semibold text-gold-ink uppercase tracking-widest mb-3">Starting Pitcher</p>
                          <div className="flex flex-wrap gap-2">
                            {arms.map(p => {
                              const isSP = gp.startingPitcherId === p.id;
                              const a = avail[p.id];
                              return (
                                <button
                                  key={p.id}
                                  onClick={() => setStartingPitcher(game.id, p.id)}
                                  className={cn(
                                    "flex items-center space-x-2 px-3 py-2 rounded-xl text-[11px] font-black transition-all border",
                                    isSP
                                      ? "bg-gold text-surface border-gold shadow-sm shadow-amber-500/20"
                                      : "bg-surface text-ink-2 border-hairline hover:border-ink-mute hover:bg-canvas"
                                  )}
                                >
                                  <span className={cn("w-1.5 h-1.5 rounded-full", statusDot(a?.status))} />
                                  <span className="truncate max-w-[120px]">{p.name}</span>
                                </button>
                              );
                            })}
                            {arms.length === 0 && (
                              <p className="text-[10px] font-bold text-ink-2 italic">No rated pitchers on the roster.</p>
                            )}
                          </div>
                        </div>

                        {/* Relief Plan */}
                        <div className="p-4 border-b border-raised">
                          <p className="text-[10px] font-semibold text-gold-ink uppercase tracking-widest mb-1">Relief Plan</p>
                          <p className="text-[9px] font-bold text-ink-2 mb-3 leading-relaxed">
                            Tap to add a reliever. Toggle “bench to start” to keep an arm fresh.
                          </p>
                          <div className="space-y-1.5">
                            {arms.filter(p => p.id !== gp.startingPitcherId).map(p => {
                              const isRelief = reliefIds.includes(p.id);
                              const isBenchRelief = benchReliefIds.includes(p.id);
                              const a = avail[p.id];
                              const order = reliefIds.indexOf(p.id);
                              return (
                                <div key={p.id} className={cn(
                                  "flex items-center justify-between px-3 py-2 rounded-xl transition-all border",
                                  isRelief
                                    ? "bg-[#FBF6E9] text-ink border-[#E8D9A8]"
                                    : "bg-surface text-ink-2 border-hairline"
                                )}>
                                  <button onClick={() => toggleRelief(game.id, p.id)} className="flex items-center space-x-2 min-w-0 flex-1 text-left">
                                    <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", statusDot(a?.status))} />
                                    {isRelief && <span className="text-[9px] font-black text-gold-ink">#{order + 1}</span>}
                                    <span className="text-[11px] font-black truncate">{p.name}</span>
                                  </button>
                                  {isRelief && (
                                    <button
                                      onClick={() => toggleBenchRelief(game.id, p.id)}
                                      className={cn(
                                        "text-[8px] font-black uppercase tracking-widest px-2 py-1 rounded-lg shrink-0 ml-2 transition-all border",
                                        isBenchRelief
                                          ? "bg-gold text-surface border-gold"
                                          : "bg-surface text-ink-2 border-hairline hover:text-ink-2"
                                      )}
                                    >
                                      {isBenchRelief ? '✓ Bench to start' : 'Bench to start'}
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Who sits to start (supporting input) */}
                        <div className="p-4 bg-canvas">
                          <p className="text-[10px] font-semibold text-gold-ink uppercase tracking-widest mb-1">Who Sits To Start</p>
                          <p className="text-[9px] font-bold text-ink-2 mb-3 leading-relaxed">
                            {gameBenchTarget > 0
                              ? <>Batting {isFinite(battingSlots) ? battingSlots : players.length}, so about {gameBenchTarget} sit{gameBenchTarget === 1 ? 's' : ''} to start. {benchedIds.length} picked.</>
                              : <>Everyone bats. Nobody starts benched (switch to a 9/10 order in Settings to plan a starting bench).</>}
                          </p>
                          {gameBenchTarget > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {sortedRoster.map(p => {
                                const isBenched = benchedIds.includes(p.id);
                                return (
                                  <button
                                    key={p.id}
                                    onClick={() => toggleBench(game.id, p.id)}
                                    className={cn(
                                      "px-3 py-1.5 rounded-lg text-[10px] font-black transition-all border",
                                      isBenched
                                        ? "bg-ink text-surface border-ink"
                                        : "bg-surface text-ink-2 border-hairline hover:text-ink-2 hover:border-ink-mute"
                                    )}
                                  >
                                    {p.name}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pitcher usage across the weekend */}
                {arms.length > 0 && (
                  <div className="bg-canvas rounded-2xl border border-hairline p-5">
                    <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-3">Starts across the round robin</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                      {arms.map(p => (
                        <div key={p.id} className="flex items-center justify-between">
                          <span className="text-[11px] font-bold text-ink-2 truncate">{p.name}</span>
                          <span className={cn("text-[11px] font-black ml-2", (pitchResult.startCountByPitcher[p.id] || 0) > 1 ? "text-gold-ink" : "text-ink")}>
                            {pitchResult.startCountByPitcher[p.id] || 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Apply */}
                <button
                  onClick={() => applyRoundRobinToGames(tournamentGames)}
                  disabled={benchApplyState === 'saving'}
                  className={cn(
                    "w-full py-4 rounded-2xl text-[11px] font-black uppercase tracking-[0.2em] transition-all flex items-center justify-center space-x-2",
                    benchApplyState === 'done'
                      ? "bg-accent-ink text-surface"
                      : "bg-gold text-surface shadow-xl shadow-amber-500/20 hover:brightness-105"
                  )}
                >
                  {benchApplyState === 'done' ? (
                    <><CheckCircle2 className="w-4 h-4" /><span>Applied to game plans</span></>
                  ) : benchApplyState === 'saving' ? (
                    <span>Applying…</span>
                  ) : (
                    <><Play className="w-4 h-4" /><span>Apply to game plans</span></>
                  )}
                </button>
                <p className="text-[9px] font-bold text-ink-2 text-center -mt-2 leading-relaxed px-4">
                  Sends each game's starting pitcher and starting bench into its game plan. Open the game and tap Fill: your starter's on the mound, benched kids start on the bench (B), and the extra batter is labeled EH. Subs by inning come next.
                </p>
              </div>
            );
          })()}

          {activeSection === 'pitching' && (() => {
            const ruleset = PITCH_COUNT_RULESETS[tournament.pitchCountRulesetOverride || team.settings?.tournamentOrg || team.settings?.pitchCountRuleset || 'little_league'] || PITCH_COUNT_RULESETS.little_league;
            const effectiveCustomRules = buildCustomTournamentRules(ruleset.id === 'custom', tournament, team.settings);
            const ageGroup = team.settings?.pitchCountAgeGroup || team.ageGroup || '12U';
            // Show all players, sorted: READY → TOURNAMENT CAPPED → REST REQUIRED
            const allPitchers = [...players].sort((a, b) => {
              // 1st: eligibility (READY → CAPPED → REST REQUIRED)
              const eligScore = (p: any) => {
                const el = getPitcherEligibility(p.pitchingLog || [], ruleset, ageGroup, new Date(), 0, tournament.id, enforceGlobalRest, effectiveCustomRules);
                const cap = getTournamentCapacity(p, ruleset, ageGroup, tournament.id, enforceGlobalRest, new Date(), effectiveCustomRules);
                if (!el.eligible) return 2;
                if (cap.isCapped) return 1;
                return 0;
              };
              const eDiff = eligScore(a) - eligScore(b);
              if (eDiff !== 0) return eDiff;
              // 2nd: players with stats before players without
              const sa = seasonPitchingStats[a.id];
              const sb = seasonPitchingStats[b.id];
              const hasA = sa?.sPct != null || sa?.p_bf != null;
              const hasB = sb?.sPct != null || sb?.p_bf != null;
              if (hasA && !hasB) return -1;
              if (!hasA && hasB) return 1;
              if (!hasA && !hasB) return 0;
              // 3rd: S% descending
              const sPctDiff = (sb?.sPct ?? -1) - (sa?.sPct ?? -1);
              if (sPctDiff !== 0) return sPctDiff;
              // 4th: P/BF ascending
              return (sa?.p_bf ?? Infinity) - (sb?.p_bf ?? Infinity);
            });

            return (
              <div className="space-y-6">
                {/* Global Rest Toggle */}
                <div className="bg-canvas rounded-2xl border border-hairline p-5 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-black text-ink uppercase tracking-tight">Show Global Rest</p>
                    <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mt-1">
                      {enforceGlobalRest ? 'League game rest applied' : 'Fresh Start: tournament logs only'}
                    </p>
                  </div>
                  <button
                    onClick={() => setEnforceGlobalRest(!enforceGlobalRest)}
                    className={cn(
                      'w-14 h-7 rounded-full transition-all relative flex items-center px-1 shadow-inner shrink-0',
                      enforceGlobalRest ? 'bg-ink' : 'bg-raised'
                    )}
                  >
                    <motion.div
                      layout
                      className={cn('w-5 h-5 bg-ink rounded-full shadow-xl', enforceGlobalRest ? 'ml-auto' : 'ml-0')}
                    />
                  </button>
                </div>

                {/* Player cards */}
                <div className="space-y-4">
                  {allPitchers.map((player, idx) => {
                    const stats = pitchingStats[player.id];
                    const todayKey = format(new Date(), 'yyyy-MM-dd');
                    const todayPitches = stats?.daily[todayKey] || 0;

                    const capacity = getTournamentCapacity(player, ruleset, ageGroup, tournament.id, enforceGlobalRest, new Date(), effectiveCustomRules);
                    const eligibility = getPitcherEligibility(
                      player.pitchingLog || [],
                      ruleset,
                      ageGroup,
                      new Date(),
                      todayPitches,
                      tournament.id,
                      enforceGlobalRest,
                      effectiveCustomRules
                    );

                    const statusLabel = !eligibility.eligible ? 'REST REQUIRED' : capacity.isCapped ? 'TOURNAMENT CAPPED' : 'READY';
                    const StatusIcon = !eligibility.eligible ? AlertTriangle : capacity.isCapped ? Minus : CheckCircle2;
                    const statusColor = !eligibility.eligible ? 'text-danger bg-[#FBF0F0] border-hairline'
                      : capacity.isCapped ? 'text-ink-2 bg-raised border-ink-mute'
                      : 'text-accent-ink bg-canvas border-hairline';

                    const sPctVal  = seasonPitchingStats[player.id]?.sPct  ?? null;
                    const p_bfVal  = seasonPitchingStats[player.id]?.p_bf  ?? null;
                    const sPctColor = sPctVal == null ? 'text-ink-2' : sPctVal >= 0.6 ? 'text-accent-ink' : sPctVal < 0.5 ? 'text-danger' : 'text-ink';
                    const p_bfColor = p_bfVal == null ? 'text-ink-2' : p_bfVal <= 3.8 ? 'text-accent-ink' : p_bfVal > 4.5 ? 'text-danger' : 'text-ink';

                    return (
                      <div key={`${player.id}-${idx}`} className="bg-canvas rounded-[2rem] border border-hairline p-6 space-y-4 hover:border-ink-mute transition-all">
                        {/* Header row */}
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-surface rounded-2xl flex flex-col items-center justify-center border border-hairline shrink-0">
                            <span className="text-sm font-black text-ink">#{player.number}</span>
                            <span className="text-[9px] font-black text-ink-2 uppercase tracking-tighter">{(player.positions && player.positions[0]) || 'UTIL'}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <h4 className="font-black uppercase text-base text-ink truncate tracking-tight">{player.name}</h4>
                              <div className={cn('flex items-center space-x-1.5 px-2.5 py-1 rounded-lg border shrink-0', statusColor)}>
                                <StatusIcon className="w-3 h-3" />
                                <span className="text-[9px] font-semibold uppercase tracking-widest">{statusLabel}</span>
                              </div>
                            </div>
                            {/* S% and P/BF */}
                            <div className="flex items-center space-x-3 mt-1.5">
                              <span className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest">S%</span>
                              <span className={cn('text-[11px] font-black', sPctColor)}>
                                {sPctVal != null ? `${Math.round(sPctVal * 100)}%` : '—'}
                              </span>
                              <span className="text-hairline text-[9px]">·</span>
                              <span className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest">P/BF</span>
                              <span className={cn('text-[11px] font-black', p_bfColor)}>
                                {p_bfVal != null ? p_bfVal.toFixed(1) : '—'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Capacity bar — only when pitcher has thrown in tournament */}
                        {stats && (
                          <div className="space-y-1.5">
                            <div className="h-2.5 bg-surface rounded-full overflow-hidden border border-hairline p-[1px]">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${Math.min(100, capacity.pct)}%` }}
                                className={cn(
                                  'h-full rounded-full',
                                  capacity.color === 'black' ? 'bg-ink-mute' :
                                  capacity.color === 'red'   ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)]' :
                                  capacity.color === 'amber' ? 'bg-gold shadow-[0_0_8px_rgba(245,158,11,0.4)]' :
                                                               'bg-ink shadow-[0_0_8px_rgba(16,185,129,0.3)]'
                                )}
                              />
                            </div>
                            <div className="flex justify-between items-center px-0.5">
                              <span className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">{stats.total} tourney pitches</span>
                              <span className="text-[10px] font-semibold text-ink uppercase tracking-widest">{capacity.remaining} remaining</span>
                            </div>
                          </div>
                        )}

                        {/* Per-game breakdown */}
                        {stats?.gameBreakdown.length > 0 && (
                          <div className="grid grid-cols-3 gap-2">
                            {stats.gameBreakdown.map((gb: any, i: number) => (
                              <div key={i} className="bg-surface p-2 rounded-xl border border-hairline">
                                <div className="text-[8px] font-bold text-ink-2 uppercase truncate">vs {gb.opponent}</div>
                                <div className="text-xs font-black text-ink-2">{gb.count} pitches</div>
                                <div className={cn('text-[8px] font-bold uppercase', gb.phase === 'bracket' ? 'text-danger' : 'text-blue-400')}>{gb.phase}</div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Rest alert */}
                        {!eligibility.eligible && eligibility.restStatus && (
                          <div className="flex items-center space-x-2 p-2.5 bg-[#FBF0F0] rounded-xl border border-red-500/10">
                            <AlertTriangle className="w-3 h-3 text-danger shrink-0" />
                            <p className="text-[9px] font-medium text-danger italic">
                              Eligible {format(new Date(eligibility.restStatus.eligibleDate), 'MMM d')} — {eligibility.restStatus.daysRemaining} {eligibility.restStatus.daysRemaining === 1 ? 'day' : 'days'} remaining
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Footer: ruleset info */}
                <div className="flex items-start space-x-3 p-4 bg-canvas rounded-2xl border border-hairline">
                  <Info className="w-4 h-4 text-ink-2 shrink-0 mt-0.5" />
                  <p className="text-[10px] font-medium text-ink-2 leading-relaxed uppercase tracking-widest italic">
                    Capacity based on <span className="text-ink-2 font-black">{ruleset.name} {ageGroup}</span> rules.
                    {ruleset.ageGroups[ageGroup]?.eventMax && (
                      <> Tournament cap: <span className="text-ink-2 font-black">{ruleset.ageGroups[ageGroup].eventMax} pitches.</span></>
                    )}
                  </p>
                </div>
              </div>
            );
          })()}
        </div>
      </main>

    </motion.div>
  );
};

const TournamentGameCard = ({
  game,
  isEditing,
  onEdit,
  onCancel,
  onOpen,
  onUpdate,
  onAddAnother,
  onImportStats,
  onEnterResults,
  onPlan
}: {
  game: any,
  isEditing: boolean,
  onEdit: () => void,
  onCancel: () => void,
  onOpen: () => void,
  onUpdate: (data: any) => void,
  onAddAnother?: () => void,
  onImportStats?: (game: any) => void,
  onEnterResults?: (game: any) => void,
  onPlan?: () => void
}) => {
  const [editData, setEditData] = useState({
    opponent: game.opponent === 'TBD' ? '' : game.opponent,
    date: game.date,
    isChampionship: !!game.isChampionship
  });

  // Sync editData when game changes (e.g. when switching slots)
  useEffect(() => {
    setEditData({
      opponent: game.opponent === 'TBD' ? '' : game.opponent,
      date: game.date,
      isChampionship: !!game.isChampionship
    });
  }, [game.id]);

  const handleSave = (action: 'save' | 'start' | 'another') => {
    onUpdate(editData);
    if (action === 'start') {
      onOpen();
    } else if (action === 'another' && onAddAnother) {
      onAddAnother();
    }
  };

  if (isEditing) {
    return (
      <div className="bg-canvas p-6 rounded-[2rem] border-2 border-gold space-y-5 shadow-2xl">
        <div className="grid gap-4">
          <div className="space-y-1.5">
            <label className="text-[10px] font-semibold uppercase text-ink-2 tracking-widest ml-1">Opponent</label>
            <input 
              value={editData.opponent}
              onChange={e => setEditData({...editData, opponent: e.target.value})}
              className="w-full bg-surface border border-hairline rounded-2xl p-4 text-sm font-bold text-ink outline-none focus:ring-2 focus:ring-amber-500/50 transition-all"
              placeholder="Enter Opponent Name"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-semibold uppercase text-ink-2 tracking-widest ml-1">Game Time</label>
            <input
              type="datetime-local"
              value={editData.date ? editData.date.slice(0, 16) : ''}
              onChange={e => setEditData({...editData, date: e.target.value})}
              className="w-full min-w-0 box-border appearance-none bg-surface border border-hairline rounded-2xl p-4 text-sm font-bold text-ink outline-none focus:ring-2 focus:ring-amber-500/50 transition-all"
            />
          </div>
          {game.tournamentPhase === 'bracket' && (
            <label className="flex items-center space-x-3 px-2 py-1 cursor-pointer group">
              <div className={cn(
                "w-5 h-5 rounded border-2 flex items-center justify-center transition-all",
                editData.isChampionship ? "bg-gold border-amber-500" : "border-hairline bg-surface group-hover:border-ink-mute"
              )}>
                {editData.isChampionship && <Trophy className="w-3 h-3 text-surface" />}
                <input 
                  type="checkbox"
                  checked={editData.isChampionship}
                  onChange={e => setEditData({...editData, isChampionship: e.target.checked})}
                  className="sr-only"
                />
              </div>
              <span className="text-[10px] font-semibold uppercase text-ink-2 tracking-widest">Championship Game</span>
            </label>
          )}
        </div>
        <div className="flex flex-col space-y-3 pt-2">
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => handleSave('start')} 
              className="flex-1 py-4 bg-ink text-surface text-[10px] font-black uppercase rounded-2xl flex items-center justify-center space-x-2 shadow-lg shadow-emerald-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all"
            >
              <Play className="w-4 h-4 fill-current" />
              <span>Start Game</span>
            </button>
            {onAddAnother && (
              <button 
                onClick={() => handleSave('another')} 
                className="flex-1 py-4 bg-gold text-surface text-[10px] font-black uppercase rounded-2xl flex items-center justify-center space-x-2 shadow-lg shadow-amber-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>Add Another</span>
              </button>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => handleSave('save')} 
              className="flex-1 py-3 bg-raised text-ink text-[10px] font-black uppercase rounded-2xl hover:bg-hairline transition-colors"
            >
              Save Slot
            </button>
            <button 
              onClick={onCancel} 
              className="px-6 py-3 bg-canvas text-ink-2 text-[10px] font-black uppercase rounded-2xl border border-hairline hover:text-ink transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-canvas p-5 rounded-[2rem] border border-hairline flex flex-col gap-4 group hover:border-hairline transition-all shadow-lg hover:shadow-amber-500/5">
      <div className="flex items-center space-x-4 min-w-0">
        <div className={cn(
          "w-14 h-14 shrink-0 rounded-2xl flex flex-col items-center justify-center transition-all",
          game.status === 'completed' ? "bg-raised" : "bg-surface border border-hairline group-hover:border-hairline"
        )}>
          <span className="text-[9px] font-black uppercase text-ink-2 tracking-tighter">{format(new Date(game.date), 'MMM')}</span>
          <span className="text-xl font-black leading-none text-ink">{format(new Date(game.date), 'd')}</span>
        </div>
        <div onClick={onEdit} className="cursor-pointer space-y-1 min-w-0">
          <div className="flex items-center space-x-2 min-w-0">
            <h4 className="font-black uppercase text-sm text-ink tracking-tight group-hover:text-gold-ink transition-colors truncate">
              {game.opponent === 'TBD' ? 'TBD Opponent' : game.opponent}
            </h4>
            {game.isChampionship && (
              <div className="bg-gold-soft p-1 rounded">
                <Trophy className="w-3 h-3 text-gold-ink" />
              </div>
            )}
          </div>
          <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-[0.2em]">
            {format(new Date(game.date), 'h:mm a')}
          </p>
        </div>
      </div>
      
      <div className="flex items-center justify-end">
        {game.status === 'completed' ? (
          <div className="flex items-center space-x-2">
            <div className={cn(
              "px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-inner",
              game.result?.outcome === 'W' ? "bg-canvas text-accent-ink border border-hairline" : "bg-[#FBF0F0] text-danger border border-hairline"
            )}>
              {game.result?.outcome} {game.result?.myScore}-{game.result?.opponentScore}
            </div>
            {onImportStats && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onImportStats(game);
                }}
                className="p-2 bg-raised text-ink-2 hover:text-accent-ink rounded-lg transition-colors"
                title="Upload Stats"
              >
                <FileUp className="w-3 h-3" />
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 w-full">
            {/* Backlogging a past game: enter the result directly — same UX as the
                Games tab. Chains into stats import on save. */}
            {onEnterResults && new Date(game.date) < new Date() && game.opponent !== 'TBD' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEnterResults(game);
                }}
                className="flex flex-1 min-w-0 items-center justify-center space-x-1.5 px-3 py-3 bg-surface border border-ink-mute text-ink rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-raised transition-colors"
                title="Enter final score without playing the game live"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Enter Results</span>
              </button>
            )}
            {/* Plan the night before: batting order, defense by inning, bullpen.
                Opens the same PlanGameView the Games tab uses — it already reads
                game.tournamentId for tournament pitch-count rules. */}
            {onPlan && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onPlan();
                }}
                className="flex flex-1 min-w-0 items-center justify-center space-x-1.5 px-3 py-3 bg-surface border border-ink-mute text-ink rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-raised transition-colors"
                title="Set the lineup, defense and bullpen for this game"
              >
                <ClipboardList className="w-3.5 h-3.5" />
                <span>Plan</span>
                {game.plan?.isReady && <CheckCircle2 className="w-3 h-3 text-accent-ink" />}
              </button>
            )}
            <button
              onClick={onOpen}
              className="px-5 py-3 shrink-0 bg-gold text-surface rounded-xl shadow-xl shadow-amber-500/20 hover:brightness-105 active:scale-95 transition-all"
            >
              <Play className="w-4 h-4 fill-current" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
